if (!customElements.get('commercial-search-results')) {
  customElements.define(
    'commercial-search-results',
    class CommercialSearchResults extends HTMLElement {
      observer = undefined;
      scheduled = false;
      lastFocusedInput = null;
      mutating = false;

      BOOST_LIST_SELECTOR = '[class*="boost-sd__instant-search-product-list-items"]';
      HOST_CLASS = 'commercial-search-results';
      FALLBACK_CLASS = 'commercial-search-results--fallback';
      TYPED_CLASS = 'tmf-commercial-search-typed';
      INPUT_SELECTOR =
        '#boost-sd__search-widget-init-input-0, input.boost-sd__search-widget-init-input, .header-search input[type="search"], .menu-drawer__search input[type="search"]';
      FALLBACK_SLOT_SELECTOR =
        '.desktop-search-wrapper, .menu-drawer__search, .header-search--in-nav';

      constructor() {
        super();
        this.onDocumentInput = this.onDocumentInput.bind(this);
        this.onDocumentFocus = this.onDocumentFocus.bind(this);
        this.onMutations = this.onMutations.bind(this);
      }

      connectedCallback() {
        if (!this.isCommercial()) return;
        this.setup();
      }

      disconnectedCallback() {
        this.teardown();
      }

      /**
       * @returns {boolean}
       */
      isCommercial() {
        return document.documentElement.classList.contains('tmf-commercial-cart');
      }

      setup() {
        document.addEventListener('input', this.onDocumentInput, true);
        document.addEventListener('focusin', this.onDocumentFocus, true);

        if (window.MutationObserver && document.body) {
          this.observer = new MutationObserver(this.onMutations);
          this.observer.observe(document.body, { childList: true, subtree: true });
        }

        this.updateTypedState();
        this.syncAndRender();
      }

      teardown() {
        document.removeEventListener('input', this.onDocumentInput, true);
        document.removeEventListener('focusin', this.onDocumentFocus, true);
        this.observer?.disconnect();
        this.observer = undefined;
        document.documentElement.classList.remove(this.TYPED_CLASS);
      }

      onMutations() {
        if (this.scheduled || this.mutating) return;
        this.scheduled = true;
        requestAnimationFrame(() => {
          this.scheduled = false;
          if (!this.isCommercial()) return;
          this.syncAndRender();
        });
      }

      /**
       * @param {Event} event
       */
      onDocumentInput(event) {
        if (!this.isSearchInput(event.target)) return;
        this.lastFocusedInput = event.target;
        this.updateTypedState();
        this.syncAndRender();
      }

      /**
       * @param {FocusEvent} event
       */
      onDocumentFocus(event) {
        if (!this.isSearchInput(event.target)) return;
        this.lastFocusedInput = event.target;
        this.updateTypedState();
        this.syncAndRender();
      }

      /**
       * @param {EventTarget | null} target
       * @returns {boolean}
       */
      isSearchInput(target) {
        if (!(target instanceof HTMLInputElement)) return false;
        if (target.matches(this.INPUT_SELECTOR)) return true;
        return target.type === 'search' || target.name === 'q';
      }

      /**
       * Whether the active search input currently has a non-empty query.
       * Boost's own live results only take over once a query is typed;
       * the curated list stays for the empty/just-opened state.
       * @returns {boolean}
       */
      hasActiveQuery() {
        const input = this.lastFocusedInput || document.querySelector(`${this.INPUT_SELECTOR}:focus`);
        return !!input && input.value.trim().length > 0;
      }

      updateTypedState() {
        document.documentElement.classList.toggle(this.TYPED_CLASS, this.hasActiveQuery());
      }

      syncAndRender() {
        this.mutating = true;
        this.syncHosts();
        this.renderAllHosts();
        this.mutating = false;
      }

      /**
       * @returns {HTMLElement}
       */
      createHost() {
        const host = document.createElement('div');
        host.className = this.HOST_CLASS;
        host.setAttribute('data-commercial-search-host', 'true');
        return host;
      }

      /**
       * Whether `el`'s own dropdown/panel ancestor is actually laid out on
       * screen right now, as opposed to merely present in the DOM. Boost's
       * search widget injects its list container (hidden by the CSS rule
       * above while the query is empty) a few seconds after the rest of the
       * widget mounts, well before it actually opens that panel. Walking the
       * computed `display` up the tree (rather than trusting the list's own,
       * intentionally-hidden style) tells the two cases apart.
       * @param {Element | null} el
       * @returns {boolean}
       */
      isVisible(el) {
        let node = el;
        while (node && node.nodeType === 1) {
          if (window.getComputedStyle(node).display === 'none') return false;
          node = node.parentElement;
        }
        return true;
      }

      syncHosts() {
        const lists = document.querySelectorAll(this.BOOST_LIST_SELECTOR);
        let openLists = 0;

        for (const list of lists) {
          const parent = list.parentElement;
          if (!parent || !this.isVisible(parent)) continue;
          openLists += 1;

          let host = parent.querySelector(`:scope > .${this.HOST_CLASS}`);
          if (!host) {
            host = this.createHost();
            list.insertAdjacentElement('afterend', host);
          }
        }

        if (openLists) {
          this.removeFallbackHosts();
          return;
        }

        if (!this.lastFocusedInput && !document.querySelector(`${this.INPUT_SELECTOR}:focus`)) {
          this.removeFallbackHosts();
          return;
        }

        this.ensureFallbackHosts();
      }

      ensureFallbackHosts() {
        const input = this.lastFocusedInput || document.querySelector(`${this.INPUT_SELECTOR}:focus`);
        const slot = input?.closest(this.FALLBACK_SLOT_SELECTOR);
        if (!slot || slot.querySelector(`.${this.HOST_CLASS}`)) return;

        const host = this.createHost();
        host.classList.add(this.FALLBACK_CLASS);
        slot.appendChild(host);
      }

      removeFallbackHosts() {
        const fallbacks = document.querySelectorAll(`.${this.FALLBACK_CLASS}`);

        for (const host of fallbacks) {
          host.remove();
        }
      }

      renderAllHosts() {
        const hosts = document.querySelectorAll('[data-commercial-search-host]');
        const typed = this.hasActiveQuery();

        for (const host of hosts) {
          if (typed) {
            this.clearCurated(host);
            continue;
          }
          if (host.querySelector('.predictive-search__results-list')) continue;
          // Rendering replaces the host's children, which is itself a
          // mutation the outer MutationObserver reacts to. Without this
          // guard, every observer tick rebuilds the list (destroying and
          // recreating the product links), which re-triggers the observer
          // forever: a constant flicker that also breaks clicks, since the
          // link a shopper is clicking can be swapped out mid-interaction.
          if (host.dataset.curatedRendered === 'true') continue;
          this.appendCurated(host);
        }
      }

      /**
       * Empties and hides the curated host once a query is typed, so Boost's
       * own (already tag-filtered) live results show through instead.
       * @param {Element} host
       */
      clearCurated(host) {
        if (!host.hidden) host.replaceChildren();
        host.hidden = true;
        delete host.dataset.curatedRendered;
      }

      /**
       * @param {Element} host
       */
      appendCurated(host) {
        const template = this.querySelector('template[data-curated]');
        const list = template?.content?.querySelector('ul');
        host.replaceChildren();
        host.hidden = false;
        host.dataset.curatedRendered = 'true';

        const heading = document.createElement('h2');
        heading.className = 'predictive-search__heading';
        heading.textContent = this.dataset.curatedHeading || '';
        host.appendChild(heading);

        if (list) {
          const clone = list.cloneNode(true);
          // Curated cards intentionally reuse Boost's own CSS classes for visual
          // parity, but that means they can also match any click-delegation Boost
          // binds on an ancestor by class name. Stop the click from bubbling past
          // this link so only native anchor navigation (and commercial-mode.liquid's
          // capture-phase href rewriter, which already ran before this point) can
          // act on it.
          clone.querySelectorAll('a[href]').forEach((link) => {
            link.addEventListener('click', (event) => event.stopPropagation());
          });
          host.appendChild(clone);
        }
      }
    }
  );
}
